<script setup>
import SubAppNavigation from "@/components/shared/Navigation/SubAppNavigation.vue";
import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { CURRICULUM_INTEGRATION_ROUTES } from "@/constants";
import whiteLabelBrandingService from "@/services/whiteLabelBrandingService";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { CI_HOME_CTA_ARIA_LABEL, CURRICULUM_INTEGRATION_LINKS } from "../constants";
const { VITE_CONNECT_URL = "http://apiconnect.apiabroad.localhost:8081" } = import.meta.env;
import { TABS, TAB_NAMES } from "@/components/ExperiencePage/Algolia/constants.js";

const props = defineProps({
  headingTitle: {
    type: String,
    required: true,
    default: "Curriculum Integration",
  },
});

const route = useRoute();

const { getSelectedEntityView: selectedEntity } = useTenantEntityManager();
const selectedEntityId = computed(() => selectedEntity?.value?.id);

const queryKey = computed(() => ["whiteLabelBranding", selectedEntityId.value]);

const { data: branding, isLoading } = useQuery({
  queryKey,
  queryFn: () => whiteLabelBrandingService.getByEntityId(selectedEntityId.value),
  enabled: !!selectedEntityId.value,
});

const isHomeRoute = computed(() => route.name === CURRICULUM_INTEGRATION_ROUTES.HOME);

const customTooltipConfig = computed(() => ({
  value: "If you've approved any courses, you can view them in the student search (opens in a new tab).",
  pt: {
    root: [
      "rounded-md shadow-lg",
      "border-0",
      "absolute left-0 top-0",
      "-translate-y-8 -translate-x-1",
      "bg-white",
      "text-black",
      "before:absolute",
      "before:left-[calc(var(--overlayArrowLeft,0)+1rem)]",
      "before:w-0",
      "before:h-0",
      "before:border-8",
      "before:border-transparent",
      "before:border-b-white",
      "before:transform",
      "before:-translate-x-1/2",
    ],
    text: "py-1 px-2",
  },
}));

const FALLBACK_COURSES_SLUG = "/results/courses";

const approvalsUrl = computed(() => {
  const url = new URL(VITE_CONNECT_URL);
  const coursesSlug = TABS.find((tab) => tab.name === TAB_NAMES.COURSES)?.slug ?? FALLBACK_COURSES_SLUG;
  url.pathname = coursesSlug;

  if (branding.value?.subdomain) {
    url.hostname = `${branding.value.subdomain}.${url.hostname}`;
  } else if (selectedEntityId.value) {
    url.searchParams.set("ci", selectedEntityId.value);
  }

  return url.toString();
});
</script>

<template>
  <div>
    <!-- Mobile Layout -->
    <div class="md:hidden">
      <div class="flex justify-between">
        <h1 class="font-montserrat font-semibold capitalize text-2xl text-indigo-base">
          {{ props.headingTitle }}
        </h1>
        <div id="mobile-menu-container" class="relative" role="navigation" aria-label="Curriculum integration menu">
          <SubAppNavigation :filtered-links="CURRICULUM_INTEGRATION_LINKS" />
        </div>
      </div>
      <a
        :href="approvalsUrl"
        target="_blank"
        rel="noopener noreferrer"
        id="mobile-view-approvals-btn"
        :aria-label="CI_HOME_CTA_ARIA_LABEL"
        :aria-disabled="isLoading"
        :class="[
          'w-full text-sm font-medium mt-4 rounded-none text-university-secondary border border-university-secondary transition-colors duration-200 py-2 px-4 text-center inline-block',
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-university-secondary hover:text-white',
        ]"
        @click="isLoading && $event.preventDefault()"
      >
        VIEW LIVE APPROVALS
      </a>
    </div>

    <!-- Desktop Layout -->
    <div class="hidden md:flex items-center justify-between">
      <h1 class="font-montserrat font-semibold capitalize text-2xl text-indigo-base">
        {{ props.headingTitle }}
      </h1>
      <div class="flex items-center gap-4">
        <a
          :href="approvalsUrl"
          target="_blank"
          rel="noopener noreferrer"
          id="desktop-view-approvals-btn"
          :aria-label="CI_HOME_CTA_ARIA_LABEL"
          :aria-disabled="isLoading"
          v-tooltip.top="customTooltipConfig"
          :class="[
            'text-sm font-medium whitespace-nowrap rounded-none text-university-secondary border border-university-secondary transition-colors duration-200 py-2 px-4 text-center inline-block',
            isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-university-secondary hover:text-white',
          ]"
          v-bind="$attrs"
          @click="isLoading && $event.preventDefault()"
        >
          VIEW LIVE APPROVALS
        </a>
        <div v-if="!isHomeRoute" role="navigation" aria-label="Curriculum integration menu">
          <SubAppNavigation :filtered-links="CURRICULUM_INTEGRATION_LINKS" />
        </div>
      </div>
    </div>
  </div>
</template>
